/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type ChangeEvent,
  type FC,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { type MatrixClient } from "matrix-js-sdk";
import {
  Button,
  InlineField,
  Label,
  RadioControl,
  Root as Form,
  Separator,
} from "@vector-im/compound-web";
import { type Room as LivekitRoom } from "livekit-client";

import { Modal } from "../Modal";
import styles from "./SettingsModal.module.css";
import { type Tab, TabContainer } from "../tabs/Tabs";
import { ProfileSettingsTab } from "./ProfileSettingsTab";
import { FeedbackSettingsTab } from "./FeedbackSettingsTab";
import { iosDeviceMenu$ } from "../state/MediaDevices";
import { useMediaDevices } from "../MediaDevicesContext";
import { widget } from "../widget";
import {
  type Setting,
  useSetting,
  soundEffectVolume as soundEffectVolumeSetting,
  backgroundBlur as backgroundBlurSetting,
  developerMode,
  allowPipSetting,
  advancedScreenShare as advancedScreenShareSetting,
  screenShareResolution as screenShareResolutionSetting,
  screenShareFramerate as screenShareFramerateSetting,
  screenShareBitrate as screenShareBitrateSetting,
  screenShareCodec as screenShareCodecSetting,
  advancedCamera as advancedCameraSetting,
  cameraResolution as cameraResolutionSetting,
  cameraFramerate as cameraFramerateSetting,
  cameraBitrate as cameraBitrateSetting,
  cameraCodec as cameraCodecSetting,
  echoCancellationSetting,
  noiseSuppressionSetting,
  autoGainControlSetting,
  type VideoCodec,
  rnnoiseNoiseSuppression as rnnoiseNoiseSuppressionSetting,
  rnnoiseNoiseSuppressionPreset as rnnoiseNoiseSuppressionPresetSetting,
  micCutoffEnabled as micCutoffEnabledSetting,
  micCutoffThresholdDb as micCutoffThresholdDbSetting,
} from "./settings";
import { PreferencesSettingsTab } from "./PreferencesSettingsTab";
import { Slider } from "../Slider";
import { DeviceSelection } from "./DeviceSelection";
import { useTrackProcessor } from "../livekit/TrackProcessorContext";
import { DeveloperSettingsTab } from "./DeveloperSettingsTab";
import { FieldRow, InputField } from "../input/Input";
import { useSubmitRageshake } from "./submit-rageshake";
import { useUrlParams } from "../UrlParams";
import { useBehavior } from "../useBehavior";
import {
  microphoneInputLevelDb$,
  supportsRNNoiseProcessor,
} from "../audio/RNNoiseProcessor";
import {
  type RNNoiseSuppressionPreset,
  rnnoiseSuppressionPresets,
} from "../audio/rnnoiseTypes";
import { MIC_CUTOFF_MAX_DB, MIC_CUTOFF_MIN_DB } from "../audio/microphoneGate";

type SettingsTab =
  | "audio"
  | "video"
  | "profile"
  | "preferences"
  | "feedback"
  | "more"
  | "developer";

interface Props {
  open: boolean;
  onDismiss: () => void;
  tab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
  client: MatrixClient;
  roomId?: string;
  livekitRooms?: {
    room: LivekitRoom;
    url: string;
    isLocal?: boolean;
  }[];
}

export const defaultSettingsTab: SettingsTab = "audio";

export const SettingsModal: FC<Props> = ({
  open,
  onDismiss,
  tab,
  onTabChange,
  client,
  roomId,
  livekitRooms,
}) => {
  const { t } = useTranslation();

  // Generate a `Checkbox` input to turn blur on or off.
  const BlurCheckbox: React.FC = (): ReactNode => {
    const { supported } = useTrackProcessor();

    const [blurActive, setBlurActive] = useSetting(backgroundBlurSetting);

    return (
      <>
        <h4>{t("settings.background_blur_header")}</h4>

        <FieldRow>
          <InputField
            id="activateBackgroundBlur"
            label={t("settings.background_blur_label")}
            description={
              supported ? "" : t("settings.blur_not_supported_by_browser")
            }
            type="checkbox"
            checked={!!blurActive}
            onChange={(b): void => setBlurActive(b.target.checked)}
            disabled={!supported}
          />
        </FieldRow>
      </>
    );
  };

  const MediaQualitySettings: React.FC<{
    id: string;
    header: string;
    toggleLabel: string;
    description: string;
    toggleSetting: Setting<boolean>;
    resolutionSetting: Setting<string>;
    framerateSetting: Setting<number>;
    bitrateSetting: Setting<number>;
    codecSetting: Setting<VideoCodec>;
    resolutionOptions: { value: string; label: string }[];
    bitrateRange: { min: number; max: number; step: number };
  }> = ({
    id,
    header,
    toggleLabel,
    description,
    toggleSetting,
    resolutionSetting,
    framerateSetting,
    bitrateSetting,
    codecSetting,
    resolutionOptions,
    bitrateRange,
  }): ReactNode => {
    const [advancedEnabled, setAdvancedEnabled] = useSetting(toggleSetting);
    const [resolution, setResolution] = useSetting(resolutionSetting);
    const [framerate, setFramerate] = useSetting(framerateSetting);
    const [framerateRaw, setFramerateRaw] = useState(framerate);
    const [bitrate, setBitrate] = useSetting(bitrateSetting);
    const [bitrateRaw, setBitrateRaw] = useState(bitrate);
    const [codec, setCodec] = useSetting(codecSetting);

    return (
      <>
        <h4>{header}</h4>
        <FieldRow>
          <InputField
            id={`${id}Toggle`}
            label={toggleLabel}
            description={description}
            type="checkbox"
            checked={advancedEnabled}
            onChange={(e): void => setAdvancedEnabled(e.target.checked)}
          />
        </FieldRow>
        {advancedEnabled && (
          <>
            <div className={styles.volumeSlider}>
              <label htmlFor={`${id}Resolution`}>
                {t("settings.resolution_label", "Resolution")}
              </label>
              <select
                id={`${id}Resolution`}
                value={resolution}
                onChange={(e): void => setResolution(e.target.value)}
              >
                {resolutionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.volumeSlider}>
              <label>
                {t("settings.framerate_label", "Framerate")}
                {": "}
                <span className={styles.settingValue}>{framerateRaw} fps</span>
              </label>
              <Slider
                label={t("settings.framerate_label", "Framerate")}
                value={framerateRaw}
                onValueChange={setFramerateRaw}
                onValueCommit={setFramerate}
                min={5}
                max={60}
                step={5}
                tooltipFormatter={(v): string => `${v} fps`}
              />
            </div>
            <div className={styles.volumeSlider}>
              <label>
                {t("settings.bitrate_label", "Bitrate")}
                {": "}
                <span className={styles.settingValue}>
                  {(bitrateRaw / 1_000_000).toFixed(1)} Mbps
                </span>
              </label>
              <Slider
                label={t("settings.bitrate_label", "Bitrate")}
                value={bitrateRaw}
                onValueChange={setBitrateRaw}
                onValueCommit={setBitrate}
                min={bitrateRange.min}
                max={bitrateRange.max}
                step={bitrateRange.step}
                tooltipFormatter={(v): string =>
                  `${(v / 1_000_000).toFixed(1)} Mbps`
                }
              />
            </div>
            <div className={styles.volumeSlider}>
              <label htmlFor={`${id}Codec`}>
                {t("settings.codec_label", "Codec")}
              </label>
              <select
                id={`${id}Codec`}
                value={codec}
                onChange={(e): void => setCodec(e.target.value as VideoCodec)}
              >
                <option value="vp8">VP8</option>
                <option value="vp9">VP9</option>
                <option value="h264">H.264</option>
                <option value="av1">AV1</option>
              </select>
            </div>
          </>
        )}
      </>
    );
  };

  const RNNoiseCheckbox: React.FC = (): ReactNode => {
    const supported = supportsRNNoiseProcessor();
    const [rnnoiseEnabled, setRnnoiseEnabled] = useSetting(
      rnnoiseNoiseSuppressionSetting,
    );
    const [rnnoisePreset, setRnnoisePreset] = useSetting(
      rnnoiseNoiseSuppressionPresetSetting,
    );
    const rnnoisePresetGroup = useId();

    const onPresetChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setRnnoisePreset(e.target.value as RNNoiseSuppressionPreset);
    };

    const presetLabelByPreset: Record<RNNoiseSuppressionPreset, string> = {
      conservative: t("settings.audio_tab.rnnoise_preset_conservative"),
      balanced: t("settings.audio_tab.rnnoise_preset_balanced"),
      strong: t("settings.audio_tab.rnnoise_preset_strong"),
    };
    const effectiveRnnoiseEnabled = supported && !!rnnoiseEnabled;

    return (
      <>
        <h4>{t("settings.audio_tab.rnnoise_header")}</h4>
        <FieldRow>
          <InputField
            id="activateRNNoiseSuppression"
            label={t("settings.audio_tab.rnnoise_label")}
            description={
              supported ? "" : t("settings.audio_tab.rnnoise_not_supported")
            }
            type="checkbox"
            checked={effectiveRnnoiseEnabled}
            onChange={(e): void => setRnnoiseEnabled(e.target.checked)}
            disabled={!supported}
          />
        </FieldRow>
        {effectiveRnnoiseEnabled && (
          <>
            <p>{t("settings.audio_tab.rnnoise_preset_description")}</p>
            {rnnoiseSuppressionPresets.map((preset) => (
              <InlineField
                key={preset}
                name={rnnoisePresetGroup}
                control={
                  <RadioControl
                    checked={rnnoisePreset === preset}
                    value={preset}
                    onChange={onPresetChange}
                    disabled={!supported}
                  />
                }
              >
                <Label>{presetLabelByPreset[preset]}</Label>
              </InlineField>
            ))}
          </>
        )}
      </>
    );
  };

  const MicrophoneCutoffSettings: React.FC = (): ReactNode => {
    const supported = supportsRNNoiseProcessor();
    const [cutoffEnabled, setCutoffEnabled] = useSetting(
      micCutoffEnabledSetting,
    );
    const [cutoffDb, setCutoffDb] = useSetting(micCutoffThresholdDbSetting);
    const [cutoffDbRaw, setCutoffDbRaw] = useState(cutoffDb);
    const effectiveCutoffEnabled = supported && !!cutoffEnabled;

    // Live input level from the mic worklet, mapped onto the slider's range.
    // Only flows while the processor is attached (i.e. during a call).
    const inputLevelDb = useBehavior(microphoneInputLevelDb$);
    const inputLevelFraction =
      inputLevelDb === null
        ? 0
        : Math.max(
            0,
            Math.min(
              1,
              (inputLevelDb - MIC_CUTOFF_MIN_DB) /
                (MIC_CUTOFF_MAX_DB - MIC_CUTOFF_MIN_DB),
            ),
          );

    return (
      <>
        <h4>{t("settings.audio_tab.mic_cutoff_header")}</h4>
        <FieldRow
          className={effectiveCutoffEnabled ? styles.cutoffFieldRow : undefined}
        >
          <InputField
            id="activateMicCutoff"
            label={t("settings.audio_tab.mic_cutoff_label")}
            description={
              supported
                ? t("settings.audio_tab.mic_cutoff_description")
                : t("settings.audio_tab.mic_cutoff_not_supported")
            }
            type="checkbox"
            checked={effectiveCutoffEnabled}
            onChange={(e): void => setCutoffEnabled(e.target.checked)}
            disabled={!supported}
          />
        </FieldRow>
        {effectiveCutoffEnabled && (
          <div className={styles.volumeSlider}>
            <label>
              {t("settings.audio_tab.mic_cutoff_threshold_label")}
              {": "}
              <span className={styles.settingValue}>{cutoffDbRaw} dB</span>
            </label>
            <Slider
              label={t("settings.audio_tab.mic_cutoff_threshold_label")}
              value={cutoffDbRaw}
              onValueChange={setCutoffDbRaw}
              onValueCommit={setCutoffDb}
              min={MIC_CUTOFF_MIN_DB}
              max={MIC_CUTOFF_MAX_DB}
              step={1}
              tooltipFormatter={(v): string => `${v} dB`}
              level={inputLevelFraction}
            />
          </div>
        )}
      </>
    );
  };

  const AudioProcessingSettings: React.FC = (): ReactNode => {
    const [echoCancellation, setEchoCancellation] = useSetting(
      echoCancellationSetting,
    );
    const [noiseSuppression, setNoiseSuppression] = useSetting(
      noiseSuppressionSetting,
    );
    const [autoGainControl, setAutoGainControl] = useSetting(
      autoGainControlSetting,
    );
    const [rnnoiseEnabled] = useSetting(rnnoiseNoiseSuppressionSetting);
    const rnnoiseSupported = supportsRNNoiseProcessor();
    const rnnoiseOverridesNative = rnnoiseEnabled && rnnoiseSupported;

    return (
      <>
        <h4>{t("settings.audio_processing_header", "Audio processing")}</h4>
        <p>
          {t(
            "settings.audio_processing_description",
            "Changes apply on next call join.",
          )}
        </p>
        <FieldRow>
          <InputField
            id="echoCancellation"
            label={t("settings.echo_cancellation_label", "Echo cancellation")}
            type="checkbox"
            checked={echoCancellation}
            onChange={(e): void => setEchoCancellation(e.target.checked)}
          />
        </FieldRow>
        <FieldRow>
          <InputField
            id="noiseSuppression"
            label={t("settings.noise_suppression_label", "Noise suppression")}
            description={
              rnnoiseOverridesNative
                ? t(
                    "settings.noise_suppression_rnnoise_override",
                    "Overridden by RNNoise.",
                  )
                : ""
            }
            type="checkbox"
            checked={!rnnoiseOverridesNative && noiseSuppression}
            onChange={(e): void => setNoiseSuppression(e.target.checked)}
            disabled={rnnoiseOverridesNative}
          />
        </FieldRow>
        <FieldRow>
          <InputField
            id="autoGainControl"
            label={t(
              "settings.auto_gain_control_label",
              "Automatic gain control",
            )}
            type="checkbox"
            checked={autoGainControl}
            onChange={(e): void => setAutoGainControl(e.target.checked)}
          />
        </FieldRow>
      </>
    );
  };

  const PipSetting: React.FC = (): ReactNode => {
    const [allowPip, setAllowPip] = useSetting(allowPipSetting);

    return (
      <>
        <FieldRow>
          <InputField
            id="allowPip"
            label={t(
              "settings.allow_pip_label",
              "Allow Browser Picture In Picture",
            )}
            type="checkbox"
            checked={allowPip}
            onChange={(e): void => setAllowPip(e.target.checked)}
          />
        </FieldRow>
      </>
    );
  };

  const devices = useMediaDevices();
  useEffect(() => {
    if (open) devices.requestDeviceNames(); // No-op after the first call
  }, [open, devices]);

  const [soundVolume, setSoundVolume] = useSetting(soundEffectVolumeSetting);
  const [soundVolumeRaw, setSoundVolumeRaw] = useState(soundVolume);
  const [showDeveloperSettingsTab] = useSetting(developerMode);

  const { available: isRageshakeAvailable } = useSubmitRageshake();

  // For controlled devices, we will not show the input section:
  // Controlled media devices are used on mobile platforms, where input and output are grouped into
  // a single device. These are called "headset" or "speaker" (or similar) but contain both input and output.
  // On EC, we decided that it is less confusing for the user if they see those options in the output section
  // rather than the input section.
  const { controlledAudioDevices } = useUrlParams();
  // If we are on iOS we will show a button to open the native audio device picker.
  const iosDeviceMenu = useBehavior(iosDeviceMenu$);

  const audioTab: Tab<SettingsTab> = {
    key: "audio",
    name: t("common.audio"),
    content: (
      <>
        <Form>
          {!controlledAudioDevices && (
            <DeviceSelection
              device={devices.audioInput}
              title={t("settings.devices.microphone")}
              numberedLabel={(n) =>
                t("settings.devices.microphone_numbered", { n })
              }
            />
          )}
          {iosDeviceMenu && controlledAudioDevices && (
            <Button
              onClick={(e): void => {
                e.preventDefault();
                window.controls.showNativeAudioDevicePicker?.();
                // call deprecated method for backwards compatibility.
                window.controls.showNativeOutputDevicePicker?.();
              }}
            >
              {t("settings.devices.change_device_button")}
            </Button>
          )}
          <DeviceSelection
            device={devices.audioOutput}
            title={t("settings.devices.speaker")}
            numberedLabel={(n) => t("settings.devices.speaker_numbered", { n })}
          />

          <div className={styles.volumeSlider}>
            <label>
              {t("settings.audio_tab.effect_volume_label")}
              {": "}
              <span className={styles.settingValue}>
                {Math.round(soundVolumeRaw * 100)}%
              </span>
            </label>
            <p>{t("settings.audio_tab.effect_volume_description")}</p>
            <Slider
              label={t("video_tile.volume")}
              value={soundVolumeRaw}
              onValueChange={setSoundVolumeRaw}
              onValueCommit={setSoundVolume}
              min={0}
              max={1}
              step={0.01}
            />
          </div>
          <Separator />
          <RNNoiseCheckbox />
          <Separator />
          <MicrophoneCutoffSettings />
        </Form>
        <Separator />
        <AudioProcessingSettings />
      </>
    ),
  };

  const videoTab: Tab<SettingsTab> = {
    key: "video",
    name: t("common.video"),
    content: (
      <>
        <PipSetting />
        <Form>
          <DeviceSelection
            device={devices.videoInput}
            title={t("settings.devices.camera")}
            numberedLabel={(n) => t("settings.devices.camera_numbered", { n })}
          />
        </Form>
        <Separator />
        <BlurCheckbox />
        <Separator />
        <MediaQualitySettings
          id="camera"
          header={t("settings.camera_header", "Camera quality")}
          toggleLabel={t(
            "settings.advanced_camera_label",
            "Advanced camera settings",
          )}
          description={t(
            "settings.advanced_camera_description",
            "Configure resolution, framerate, bitrate, and codec for camera video. Changes apply on next call join.",
          )}
          toggleSetting={advancedCameraSetting}
          resolutionSetting={cameraResolutionSetting}
          framerateSetting={cameraFramerateSetting}
          bitrateSetting={cameraBitrateSetting}
          codecSetting={cameraCodecSetting}
          resolutionOptions={[
            { value: "640x360", label: "360p" },
            { value: "960x540", label: "540p" },
            { value: "1280x720", label: "720p" },
            { value: "1920x1080", label: "1080p" },
            { value: "2560x1440", label: "1440p" },
          ]}
          bitrateRange={{ min: 200_000, max: 8_000_000, step: 100_000 }}
        />
        <Separator />
        <MediaQualitySettings
          id="screenShare"
          header={t("settings.screen_share_header", "Screen sharing")}
          toggleLabel={t(
            "settings.advanced_screen_share_label",
            "Advanced screen share settings",
          )}
          description={t(
            "settings.advanced_screen_share_description",
            "Configure resolution, framerate, bitrate, and codec for screen sharing",
          )}
          toggleSetting={advancedScreenShareSetting}
          resolutionSetting={screenShareResolutionSetting}
          framerateSetting={screenShareFramerateSetting}
          bitrateSetting={screenShareBitrateSetting}
          codecSetting={screenShareCodecSetting}
          resolutionOptions={[
            { value: "1024x576", label: "576p" },
            { value: "1280x720", label: "720p" },
            { value: "1920x1080", label: "1080p" },
            { value: "2560x1440", label: "1440p" },
            { value: "3840x2160", label: "4K" },
          ]}
          bitrateRange={{ min: 500_000, max: 15_000_000, step: 500_000 }}
        />
      </>
    ),
  };

  const preferencesTab: Tab<SettingsTab> = {
    key: "preferences",
    name: t("common.preferences"),
    content: <PreferencesSettingsTab />,
  };

  const profileTab: Tab<SettingsTab> = {
    key: "profile",
    name: t("common.profile"),
    content: <ProfileSettingsTab client={client} />,
  };

  const feedbackTab: Tab<SettingsTab> = {
    key: "feedback",
    name: t("settings.feedback_tab_title"),
    content: <FeedbackSettingsTab roomId={roomId} />,
  };

  const developerTab: Tab<SettingsTab> = {
    key: "developer",
    name: t("settings.developer_tab_title"),
    content: (
      <DeveloperSettingsTab
        env={import.meta.env}
        client={client}
        livekitRooms={livekitRooms}
        roomId={roomId}
      />
    ),
  };

  const tabs = [audioTab, videoTab];
  if (widget === null) tabs.push(profileTab);
  tabs.push(preferencesTab);
  if (isRageshakeAvailable || import.meta.env.VITE_PACKAGE === "full") {
    // for full package we want to show the analytics consent checkbox
    // even if rageshake is not available
    tabs.push(feedbackTab);
  }
  if (showDeveloperSettingsTab) tabs.push(developerTab);

  return (
    <Modal
      title={t("common.settings")}
      className={styles.settingsModal}
      open={open}
      onDismiss={onDismiss}
      tabbed
    >
      <TabContainer
        label={t("common.settings")}
        tab={tab}
        onTabChange={onTabChange}
        tabs={tabs}
      />
    </Modal>
  );
};
